import { useEffect, useRef, useState } from "react";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import {
  Form,
  FormSubmit,
  InputField,
  Modal,
  ConfirmationModal,
  useConfirmationModal,
} from "@synota-io/synota-shared-ui";
import { IContractDetailsResponse } from "../../../../api/hooks/useContractDetails";
import { useContractLastEnergyDate } from "../../../../api/hooks/useContractLastEnergyDate";
import { useHttp } from "../../../../api/http/useHttp";
import {
  currentLastEnergyDate,
  energyDateCutoff,
  validateLastEnergyDate,
} from "../utils/lastEnergyDate";

const REJECTION_MESSAGES: Partial<Record<number, string>> = {
  400: "The date was rejected. No contract copies were changed. Refresh the contract and choose a valid date on or after both contract begin dates.",
  401: "MFA verification failed. Enter a valid, unexpired code to continue.",
  403: "You do not have permission to change this date. No contract copies were changed. Contact an administrator for access.",
  404: "The contract could not be found. No contract copies were changed. Refresh the contract list before trying again.",
};

export function LastEnergyDateEditor({
  contract,
  disabled,
}: {
  contract: IContractDetailsResponse;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState<{
    severity: "success" | "error" | "warning";
    text: string;
  } | null>(null);
  const confirmation = useConfirmationModal();
  const { totp } = useHttp();
  const closeMfa = useRef(totp.onClose);
  closeMfa.current = totp.onClose;
  const editorOpen = useRef(false);
  const active = useRef(true);
  const ownsMfa = useRef(false);
  // A background refetch disables new edits, but must not cancel a confirmed MFA submission.
  // The parent unmounts this editor when the contract or write-admin access changes.
  const mutation = useContractLastEnergyDate(() => active.current && editorOpen.current);
  const { control, handleSubmit, reset } = useForm<{ date: string }>({
    defaultValues: { date: currentLastEnergyDate(contract.end_time) },
    resolver: async (values) => {
      const valid = validateLastEnergyDate(values.date, contract.begin_time);
      return valid === true
        ? { values, errors: {} }
        : { values: {}, errors: { date: { type: "validate", message: valid } } };
    },
  });
  // Parent keys this editor by contract UUID. Invalidate stale MFA callbacks on navigation.
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      if (ownsMfa.current) closeMfa.current();
    };
  }, []);

  const close = () => {
    if (mutation.isPending) return;
    if (ownsMfa.current) closeMfa.current();
    ownsMfa.current = false;
    editorOpen.current = false;
    setOpen(false);
  };
  const submit = handleSubmit(({ date }) => {
    if (disabled || mutation.isPending) return;
    confirmation.onOpen({
      title: "Set last energy date?",
      acceptLabel: "Confirm and verify MFA",
      body: (
        <Stack gap={2}>
          <Typography>
            Change <strong>{contract.consumer_contract_name || contract.shared_uuid}</strong> for
            both the consumer and supplier.
          </Typography>
          <Typography>
            Last energy date: <strong>{date} (UTC, inclusive)</strong>.<br />
            Energy cutoff: <strong>{energyDateCutoff(date)}</strong> (exclusive).
          </Typography>
          <Alert severity="warning">
            This changes when contract energy settlement ends. A past date can end an active
            contract immediately. Existing invoices and payments are not reversed. Both contract
            copies must be updated.
          </Alert>
        </Stack>
      ),
      onConfirm: () => {
        confirmation.onClose();
        ownsMfa.current = true;
        setOutcome(null);
        mutation.mutate(
          { contract_shared_uuid: contract.shared_uuid, last_energy_date: date },
          {
            onSuccess: ({ data }) => {
              ownsMfa.current = false;
              const consumer =
                data.results?.find((result) => result.party === "consumer")?.updated === true;
              const supplier =
                data.results?.find((result) => result.party === "supplier")?.updated === true;
              setOutcome(
                consumer && supplier
                  ? {
                      severity: "success",
                      text: `Last energy date saved as ${date} (UTC) on both contract copies.`,
                    }
                  : {
                      severity: "warning",
                      text: `Update incomplete. Consumer: ${consumer ? "saved" : "not confirmed"}; supplier: ${supplier ? "saved" : "not confirmed"}. The copies may differ. Refresh and retry the same date to finish updating both.`,
                    },
              );
            },
            onError: (error) => {
              const status = error.response?.status;
              if (status !== 401 && ownsMfa.current) {
                closeMfa.current();
                ownsMfa.current = false;
              }
              setOutcome({
                severity: "error",
                text:
                  (status === undefined ? undefined : REJECTION_MESSAGES[status]) ??
                  "Save could not be confirmed. One or both copies may have changed. Refresh and retry the same date; contact support if this persists.",
              });
            },
          },
        );
      },
    });
  });

  return (
    <>
      <Button
        variant="outlined"
        disabled={disabled}
        onClick={() => {
          reset({ date: currentLastEnergyDate(contract.end_time) });
          setOutcome(null);
          editorOpen.current = true;
          setOpen(true);
        }}
      >
        Set last energy date
      </Button>
      <Modal title="Last energy date" open={open} onClose={close} fullWidth maxWidth="sm">
        <Form onSubmit={submit}>
          <Stack gap={2}>
            <Typography>{contract.consumer_contract_name || contract.shared_uuid}</Typography>
            <Typography variant="body2">
              Current last energy date (UTC):{" "}
              {currentLastEnergyDate(contract.end_time) || "Not set"}
            </Typography>
            <InputField
              control={control}
              name="date"
              label="Last energy date (UTC)"
              type="date"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              disabled={disabled || mutation.isPending}
            />
            <Typography variant="body2">
              Includes the selected UTC day. Energy settlement stops at midnight UTC the following
              day.
            </Typography>
            {outcome && <Alert severity={outcome.severity}>{outcome.text}</Alert>}
            <FormSubmit disabled={disabled} isLoading={mutation.isPending}>
              Review change
            </FormSubmit>
          </Stack>
        </Form>
      </Modal>
      <ConfirmationModal {...confirmation} />
    </>
  );
}
